import React, { useState, useEffect } from 'react';
import './Documentation.css';

const Documentation = () => {
    const [activeSection, setActiveSection] = useState('overview');
    const [copiedCode, setCopiedCode] = useState('');

    const copyToClipboard = (code, id) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopiedCode(id);
            setTimeout(() => setCopiedCode(''), 2000);
        });
    };

    const CodeBlock = ({ code, id, language = 'html' }) => (
        <div className="code-block">
            <button 
                className={`copy-btn ${copiedCode === id ? 'copied' : ''}`}
                onClick={() => copyToClipboard(code, id)}
            >
                {copiedCode === id ? 'Copied!' : 'Copy'}
            </button>
            <pre><code className={language}>{code}</code></pre>
        </div>
    );

    const ConfigTable = ({ data }) => (
        <table className="config-table">
            <thead>
                <tr>
                    <th>Attribute</th>
                    <th>Type</th>
                    <th>Default</th>
                    <th>Description</th>
                </tr>
            </thead>
            <tbody>
                {data.map((row, index) => (
                    <tr key={index}>
                        <td><code>{row.attribute}</code></td>
                        <td><span className="type">{row.type}</span></td>
                        <td><span className="default">{row.default}</span></td>
                        <td>{row.description}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    const Alert = ({ type, children }) => (
        <div className={`alert alert-${type}`}>
            {children}
        </div>
    );

    const FeatureCard = ({ icon, title, description }) => (
        <div className="feature-card">
            <h4>{icon} {title}</h4>
            <p>{description}</p>
        </div>
    );

    const navigationItems = [
        { id: 'overview', label: 'Overview' },
        { id: 'search-widget', label: 'Search Widget' },
        { id: 'popup-widget', label: 'Popup Widget' },
        { id: 'search-input-widget', label: 'Search Input Widget' },
        { id: 'auto-placement', label: 'Auto Placement' },
        { id: 'advanced', label: 'Advanced Config' },
        { id: 'examples', label: 'Live Examples' },
        { id: 'troubleshooting', label: 'Troubleshooting' }
    ];

    const searchWidgetConfig = [
        {
            attribute: 'data-theme',
            type: 'string',
            default: '"dark"',
            description: 'Widget theme: "dark" or "light"'
        },
        {
            attribute: 'data-size',
            type: 'string',
            default: '"normal"',
            description: 'Button size: "normal" or "compact"'
        },
        {
            attribute: 'data-max-items',
            type: 'number',
            default: '5',
            description: 'Maximum number of search terms to display'
        }
    ];

    const popupWidgetConfig = [
        {
            attribute: 'data-delay',
            type: 'number',
            default: '5',
            description: 'Seconds before popup appears'
        },
        {
            attribute: 'data-max-items',
            type: 'number',
            default: '5',
            description: 'Maximum number of search terms to display'
        },
        {
            attribute: 'data-theme',
            type: 'string',
            default: '"dark"',
            description: 'Widget theme: "dark" or "light"'
        },
        {
            attribute: 'data-position',
            type: 'string',
            default: '"center"',
            description: 'Popup position: "center", "top-left", "top-right", "bottom-left", "bottom-right"'
        }
    ];

    const searchInputConfig = [
        {
            attribute: 'data-theme',
            type: 'string',
            default: '"dark"',
            description: 'Widget theme: "dark" or "light"'
        },
        {
            attribute: 'data-placeholder',
            type: 'string',
            default: '"Type your search here..."',
            description: 'Custom placeholder text for the input field'
        }
    ];

    const basicSearchWidget = `<!-- Add this HTML where you want the widget to appear -->
<div class="search-widget-container"></div>

<!-- Include the script before closing </body> tag -->
<script src="https://a.lukkol.com/search-widget.js"></script>`;

    const advancedSearchWidget = `<!-- Customized Search Widget -->
<div class="search-widget-container"
     data-theme="light"
     data-size="compact"
     data-max-items="3">
</div>`;

    const basicPopupWidget = `<!-- Include the script before closing </body> tag -->
<script src="https://a.lukkol.com/popup-widget.js"
        data-delay="5"
        data-max-items="5"
        data-theme="dark"
        data-position="center">
</script>`;

    const basicSearchInputWidget = `<!-- Add this HTML where you want the widget to appear -->
<div class="search-input-widget-container"></div>

<!-- Include the script before closing </body> tag -->
<script src="https://a.lukkol.com/search-input-widget.js"></script>`;

    const advancedSearchInputWidget = `<!-- Customized Search Input Widget -->
<div class="search-input-widget-container"
     data-theme="light"
     data-placeholder="Search for products...">
</div>`;

    const multipleWidgetsExample = `<!DOCTYPE html>
<html>
<head>
    <title>My Website with Lukkol Widgets</title>
</head>
<body>
    <!-- Your website content -->
    
    <!-- Search Widget -->
    <div class="search-widget-container" 
         data-theme="dark"
         data-max-items="4">
    </div>
    
    <!-- Search Input Widget -->
    <div class="search-input-widget-container"
         data-theme="dark"
         data-placeholder="Search anything...">
    </div>
    
    <!-- Include all widget scripts -->
    <script src="https://a.lukkol.com/search-widget.js"></script>
    <script src="https://a.lukkol.com/search-input-widget.js"></script>
    <script src="https://a.lukkol.com/popup-widget.js"
            data-delay="3"
            data-theme="dark"
            data-position="bottom-right">
    </script>
</body>
</html>`;

    const renderSection = () => {
        switch (activeSection) {
            case 'overview':
                return (
                    <div>
                        <h2>Overview</h2>
                        <p>Lukkol Search Widgets are enterprise-grade, lightweight, and easy-to-integrate search tools that enhance user engagement on any website. These widgets provide relevant search suggestions and seamless search functionality without requiring any complex setup or technical expertise.</p>

                        <div className="feature-grid">
                            <FeatureCard
                                icon="🔍"
                                title="Search Widget"
                                description="Displays related search terms as clickable buttons that redirect users to search results. Perfect for showing trending searches or related topics."
                            />
                            <FeatureCard
                                icon="🚀"
                                title="Popup Widget"
                                description="Shows search suggestions in a popup overlay that appears after a specified delay. Great for capturing user attention and increasing engagement."
                            />
                            <FeatureCard
                                icon="⌨️"
                                title="Search Input Widget"
                                description="Allows users to type their search terms and redirects them to search results. Ideal for providing direct search functionality."
                            />
                        </div>

                        <Alert type="info">
                            <strong>Pro Tip:</strong> All three widgets can be used together on the same page without any conflicts. They automatically initialize and work seamlessly with your existing website infrastructure.
                        </Alert>
                    </div>
                );

            case 'search-widget':
                return (
                    <div>
                        <h2>Search Widget</h2>
                        <p>The Search Widget displays related search terms as beautiful, clickable buttons that redirect users to search results. It automatically fetches trending keywords and presents them in an attractive, mobile-responsive interface that matches your brand.</p>

                        <h3>Basic Implementation</h3>
                        <CodeBlock code={basicSearchWidget} id="search-basic" />

                        <h3>Configuration Options</h3>
                        <ConfigTable data={searchWidgetConfig} />

                        <h3>Advanced Configuration</h3>
                        <CodeBlock code={advancedSearchWidget} id="search-advanced" />

                        <div className="widget-demo">
                            <h4>Live Demo - Search Widget</h4>
                            <div className="search-widget-container" data-theme="dark" data-size="normal" data-max-items="5"></div>
                        </div>
                    </div>
                );

            case 'popup-widget':
                return (
                    <div>
                        <h2>🚀 Popup Widget</h2>
                        <p>The Popup Widget displays search suggestions in an elegant popup overlay that appears after a specified delay. It's perfect for capturing user attention and increasing engagement without being intrusive.</p>

                        <h3>Basic Implementation</h3>
                        <CodeBlock code={basicPopupWidget} id="popup-basic" />

                        <h3>Configuration Options</h3>
                        <ConfigTable data={popupWidgetConfig} />

                        <h3>Position Options</h3>
                        <CodeBlock 
                            code={`<!-- Different popup positions -->
<!-- Center (default) -->
<script data-position="center"></script>

<!-- Top Right -->
<script data-position="top-right"></script>

<!-- Bottom Left -->
<script data-position="bottom-left"></script>`} 
                            id="popup-positions" 
                        />

                        <Alert type="warning">
                            <strong>⚠️ Note:</strong> The popup widget automatically prevents multiple popups from appearing on the same page. Only one popup will show per page load.
                        </Alert>
                    </div>
                );

            case 'search-input-widget':
                return (
                    <div>
                        <h2>Search Input Widget</h2>
                        <p>The Search Input Widget provides a beautiful search interface where users can type their search terms and be redirected to search results. It features a modern design with smooth animations and responsive behavior.</p>

                        <h3>Basic Implementation</h3>
                        <CodeBlock code={basicSearchInputWidget} id="input-basic" />

                        <h3>Configuration Options</h3>
                        <ConfigTable data={searchInputConfig} />

                        <h3>Advanced Configuration</h3>
                        <CodeBlock code={advancedSearchInputWidget} id="input-advanced" />

                        <div className="widget-demo">
                            <h4>Live Demo - Search Input Widget</h4>
                            <div className="search-input-widget-container" data-theme="dark"></div>
                        </div>
                    </div>
                );

            case 'auto-placement':
                return (
                    <div>
                        <h2>Auto Placement</h2>
                        <p>Automatically detect and place widgets in empty spaces on your website. This advanced feature intelligently finds suitable locations and places widgets without manual configuration.</p>

                        <Alert type="info">
                            <strong>Smart Detection:</strong> The auto-placement system analyzes your website layout, detects empty spaces, and intelligently places widgets where they'll be most effective.
                        </Alert>

                        <h3>How Auto Placement Works</h3>
                        <div className="feature-grid">
                            <FeatureCard
                                icon="🔍"
                                title="Space Detection"
                                description="Automatically scans your website for empty spaces, sidebars, and content gaps where widgets can be placed effectively."
                            />
                            <FeatureCard
                                icon="📐"
                                title="Layout Analysis"
                                description="Analyzes your website's layout structure to determine the best placement locations without disrupting existing content."
                            />
                            <FeatureCard
                                icon="🎯"
                                title="Smart Positioning"
                                description="Intelligently positions widgets based on content flow, user behavior patterns, and responsive design principles."
                            />
                        </div>

                        <h3>Basic Auto Placement</h3>
                        <CodeBlock 
                            code={`<!-- Auto Placement - Basic Setup -->
<script src="https://a.lukkol.com/auto-placement-widget.js"
        data-widget-type="search"
        data-auto-detect="true"
        data-min-space="300"
        data-max-widgets="3">
</script>`} 
                            id="auto-basic" 
                        />

                        <h3>Advanced Auto Placement Configuration</h3>
                        <CodeBlock 
                            code={`<!-- Auto Placement - Advanced Configuration -->
<script src="https://a.lukkol.com/auto-placement-widget.js"
        data-widget-type="search"
        data-auto-detect="true"
        data-min-space="250"
        data-max-widgets="5"
        data-exclude-selectors=".header, .footer, .sidebar"
        data-priority-zones=".content, .main, .article"
        data-responsive="true"
        data-delay="1000">
</script>`} 
                            id="auto-advanced" 
                        />

                        <h3>Auto Placement Configuration Options</h3>
                        <table className="config-table">
                            <thead>
                                <tr>
                                    <th>Attribute</th>
                                    <th>Type</th>
                                    <th>Default</th>
                                    <th>Description</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td><code>data-widget-type</code></td>
                                    <td><span className="type">string</span></td>
                                    <td><span className="default">"search"</span></td>
                                    <td>Type of widget to place: "search", "popup", "input"</td>
                                </tr>
                                <tr>
                                    <td><code>data-auto-detect</code></td>
                                    <td><span className="type">boolean</span></td>
                                    <td><span className="default">true</span></td>
                                    <td>Enable automatic space detection</td>
                                </tr>
                                <tr>
                                    <td><code>data-min-space</code></td>
                                    <td><span className="type">number</span></td>
                                    <td><span className="default">300</span></td>
                                    <td>Minimum space size (px) required for widget placement</td>
                                </tr>
                                <tr>
                                    <td><code>data-max-widgets</code></td>
                                    <td><span className="type">number</span></td>
                                    <td><span className="default">3</span></td>
                                    <td>Maximum number of widgets to place automatically</td>
                                </tr>
                                <tr>
                                    <td><code>data-exclude-selectors</code></td>
                                    <td><span className="type">string</span></td>
                                    <td><span className="default">""</span></td>
                                    <td>CSS selectors for areas to exclude from placement</td>
                                </tr>
                                <tr>
                                    <td><code>data-priority-zones</code></td>
                                    <td><span className="type">string</span></td>
                                    <td><span className="default">""</span></td>
                                    <td>CSS selectors for preferred placement areas</td>
                                </tr>
                                <tr>
                                    <td><code>data-responsive</code></td>
                                    <td><span className="type">boolean</span></td>
                                    <td><span className="default">true</span></td>
                                    <td>Enable responsive placement adjustments</td>
                                </tr>
                                <tr>
                                    <td><code>data-delay</code></td>
                                    <td><span className="type">number</span></td>
                                    <td><span className="default">1000</span></td>
                                    <td>Delay in milliseconds before placement analysis</td>
                                </tr>
                            </tbody>
                        </table>

                        <h3>Placement Strategies</h3>
                        <div className="feature-grid">
                            <FeatureCard
                                icon="📱"
                                title="Responsive Placement"
                                description="Automatically adjusts widget placement based on screen size and device type for optimal user experience."
                            />
                            <FeatureCard
                                icon="🎨"
                                title="Content-Aware"
                                description="Analyzes surrounding content to ensure widgets complement rather than interfere with your website's design."
                            />
                            <FeatureCard
                                icon="⚡"
                                title="Performance Optimized"
                                description="Minimal impact on page load times with efficient space detection algorithms."
                            />
                        </div>

                        <h3>Use Cases</h3>
                        <Alert type="success">
                            <strong>Perfect for:</strong>
                            <ul style={{ marginTop: '10px' }}>
                                <li>Blogs with varying content lengths</li>
                                <li>E-commerce sites with product listings</li>
                                <li>News websites with article layouts</li>
                                <li>Portfolio sites with dynamic content</li>
                                <li>Any website with flexible layouts</li>
                            </ul>
                        </Alert>

                        <h3>Manual Override</h3>
                        <p>You can still manually place widgets in specific locations while using auto-placement for other areas:</p>
                        <CodeBlock 
                            code={`<!-- Manual placement in specific areas -->
<div class="search-widget-container" data-manual="true"></div>

<!-- Auto placement for remaining spaces -->
<script src="https://a.lukkol.com/auto-placement-widget.js"
        data-widget-type="search"
        data-auto-detect="true"
        data-exclude-selectors="[data-manual='true']">
</script>`} 
                            id="auto-manual" 
                        />

                        <Alert type="warning">
                            <strong>Best Practices:</strong>
                            <ul style={{ marginTop: '10px' }}>
                                <li>Test auto-placement on different screen sizes</li>
                                <li>Use exclude selectors to prevent placement in critical areas</li>
                                <li>Set appropriate minimum space requirements</li>
                                <li>Monitor widget performance and adjust settings as needed</li>
                            </ul>
                        </Alert>
                    </div>
                );

            case 'advanced':
                return (
                    <div>
                        <h2>⚙️ Advanced Configuration</h2>
                        <p>For developers who need more control over the widgets, here are advanced configuration options and integration methods.</p>

                        <h3>Using Multiple Widgets Together</h3>
                        <CodeBlock code={multipleWidgetsExample} id="multiple-widgets" />

                        <h3>Manual Initialization (Advanced)</h3>
                        <CodeBlock 
                            code={`<!-- For advanced users who want manual control -->
<div id="mySearchWidget"></div>

<script>
    // Manual initialization with custom configuration
    const widget = new SearchWidget({
        containerId: 'mySearchWidget',
        theme: 'light',
        size: 'compact',
        maxItems: 3
    });
</script>`} 
                            id="manual-init" 
                        />

                          
                    </div>
                );

            case 'examples':
                return (
                    <div>
                        <h2>🎯 Live Examples</h2>
                        <p>Here are live examples of all widgets in action. You can see how they look and behave with different configurations.</p>

                        <div className="feature-grid">
                            <div className="feature-card">
                                <h4>🔍 Search Widget - Light Theme</h4>
                                <div className="search-widget-container" data-theme="light" data-size="compact" data-max-items="3"></div>
                            </div>
                            <div className="feature-card">
                                <h4>⌨️ Search Input - Light Theme</h4>
                                <div className="search-input-widget-container" data-theme="light" data-placeholder="Search products..."></div>
                            </div>
                        </div>

                        <Alert type="success">
                            <strong>✅ Test It Out:</strong> Try clicking on the search terms or typing in the search input to see how the widgets work in real-time!
                        </Alert>
                    </div>
                );

            case 'troubleshooting':
                return (
                    <div>
                        <h2>🛠️ Troubleshooting</h2>
                        <p>Common issues and their solutions to help you get the widgets working perfectly.</p>

                        <h3>Widget Not Appearing</h3>
                        <Alert type="warning">
                            <strong>Problem:</strong> Widget doesn't show up on the page.<br />
                            <strong>Solutions:</strong>
                            <ul style={{ marginTop: '10px' }}>
                                <li>Check that the script URL is correct: <code>https://a.lukkol.com/[widget-name].js</code></li>
                                <li>Ensure the HTML container has the correct class name</li>
                                <li>Check browser console for JavaScript errors</li>
                                <li>Make sure the script is placed before the closing <code>&lt;/body&gt;</code> tag</li>
                            </ul>
                        </Alert>

                        <h3>Styling Issues</h3>
                        <Alert type="warning">
                            <strong>Problem:</strong> Widget doesn't match your website's design.<br />
                            <strong>Solutions:</strong>
                            <ul style={{ marginTop: '10px' }}>
                                <li>Try switching themes: <code>data-theme="light"</code> or <code>data-theme="dark"</code></li>
                                <li>For Search Widget, try different sizes: <code>data-size="compact"</code></li>
                                <li>Check if your website's CSS is conflicting with the widget styles</li>
                            </ul>
                        </Alert>

                        <h3>Popup Not Showing</h3>
                        <Alert type="warning">
                            <strong>Problem:</strong> Popup widget doesn't appear after the delay.<br />
                            <strong>Solutions:</strong>
                            <ul style={{ marginTop: '10px' }}>
                                <li>Increase the delay: <code>data-delay="10"</code></li>
                                <li>Check if popup blockers are enabled</li>
                                <li>Ensure the script is loaded correctly</li>
                            </ul>
                        </Alert>

                        <h3>Search Not Working</h3>
                        <Alert type="warning">
                            <strong>Problem:</strong> Clicking search terms doesn't redirect properly.<br />
                            <strong>Solutions:</strong>
                            <ul style={{ marginTop: '10px' }}>
                                <li>Check browser console for errors</li>
                                <li>Ensure popup blockers aren't blocking new tabs</li>
                                <li>Verify the search URL is accessible</li>
                            </ul>
                        </Alert>
                    </div>
                );

            default:
                return null;
        }
    };

    useEffect(() => {
        // Load widget scripts for live demos
        const loadScript = (src) => {
            if (!document.querySelector(`script[src="${src}"]`)) {
                const script = document.createElement('script');
                script.src = src;
                document.body.appendChild(script);
            }
        };

        loadScript('https://a.lukkol.com/search-widget.js');
        loadScript('https://a.lukkol.com/search-input-widget.js');
    }, []);

    return (
        <div className="documentation-container">
        <div className="header">
            <h1>Lukkol Widgets</h1>
            <p>Professional Search Widgets for Modern Websites</p>
            <div>
                <span className="badge">Easy Integration</span>
                <span className="badge">Mobile Responsive</span>
                <span className="badge">Zero Configuration</span>
                <span className="badge">Multiple Themes</span>
                <span className="badge">Enterprise Ready</span>
            </div>
        </div>

            <div className="nav">
                <ul>
                    {navigationItems.map(item => (
                        <li key={item.id}>
                            <a 
                                href={`#${item.id}`}
                                className={activeSection === item.id ? 'active' : ''}
                                onClick={(e) => {
                                    e.preventDefault();
                                    setActiveSection(item.id);
                                }}
                            >
                                {item.label}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="section">
                {renderSection()}
            </div>

            <div className="section">
                <h2>📞 Support & Resources</h2>
                <div className="feature-grid">
                    <FeatureCard
                        icon="📧"
                        title="Support"
                        description="Need help? Contact our support team at support@lukkol.com for assistance with integration or customization."
                    />
                    <FeatureCard
                        icon="🔗"
                        title="Demo Pages"
                        description="Visit our demo pages to see the widgets in action: search-widget.html, popup-widget.html, search-input-widget.html"
                    />
                    <FeatureCard
                        icon="📚"
                        title="Documentation"
                        description="This documentation is always up-to-date. Bookmark this page for quick reference during integration."
                    />
                </div>
            </div>
        </div>
    );
};

export default Documentation;
